import React, { FC, ReactElement } from 'react';
import styles from './styles.module.css';
import { FormInput } from 'Shared/components';
import classnames from 'classnames';
import { PaymentMethodType } from '@clearcover/core-app-types';
import { paymentIcon } from 'Shared/constants/paymentIcon';
import { Asset, Button, Modal, PlusIcon } from '@clearcover/atlas';
import { AddPaymentMethodForm } from 'Policies/components/AddPaymentMethodForm';

export type PolicyPaymentMethodPropsType = {
  policyPaymentMethods: Array<PaymentMethodType>;
  value: string;
  setValue: (value: string) => void;
  disabled?: boolean;
  showAddPaymentMethod?: boolean;
  fetchBillingData?: () => Promise<void>;
};

const removeSlash = (str: string) => str.replace(/\//g, '');

export const PolicyPaymentMethod: FC<PolicyPaymentMethodPropsType> = ({
  policyPaymentMethods,
  value,
  setValue,
  disabled = false,
  showAddPaymentMethod = false,
  fetchBillingData,
}) => {
  const paymentOptions = policyPaymentMethods.reduce<{
    [k: string]: ReactElement;
  }>((acc, paymentMethod, index) => {
    const creditCardType =
      paymentMethod.type === 'creditCard'
        ? paymentMethod.cardType
        : paymentMethod.type;
    acc[paymentMethod.id] = (
      <div key={index} className={styles.paymentItem}>
        {creditCardType && (
          <Asset
            icon={paymentIcon[creditCardType]}
            description="credit card"
            height={12}
            width={12}
          />
        )}
        <div className={styles.paymentInfo}>
          {`•••• •••• •••• ${paymentMethod?.lastFour}`}
          <div className={styles.expiration}>
            {paymentMethod.type === 'creditCard' && (
              <div>{`Expires: ${removeSlash(
                paymentMethod.expirationMonth
              )}/${removeSlash(paymentMethod.expirationYear)}`}</div>
            )}
          </div>
        </div>
      </div>
    );
    return acc;
  }, {});

  return (
    <div
      className={classnames('fs-exclude', styles.creditCardSelect, {
        disabled,
      })}
    >
      <FormInput
        variant="select"
        name="payment-method-input"
        onChange={setValue}
        value={value}
        label="Saved payment methods"
        data-testid="payment-method-input"
        options={paymentOptions}
        disabled={disabled}
      />
      {showAddPaymentMethod && fetchBillingData && (
        <Modal
          variant="full"
          data-testid="payment-method-add-method-modal"
          toggle={open => (
            <Button
              variant="secondary"
              onClick={open}
              data-testid="payment-method-add-method"
            >
              <Asset
                icon={PlusIcon}
                description="Add payment method"
                height={12}
                width={12}
                className={styles.addIcon}
              />
              Add new payment method
            </Button>
          )}
        >
          {close => (
            <AddPaymentMethodForm
              close={close}
              isProcessOne={false}
              hasAdyenPaymentMethod={policyPaymentMethods.some(
                method => method.paymentProcessor === 'ADYEN'
              )}
              fetchBillingData={fetchBillingData}
            />
          )}
        </Modal>
      )}
    </div>
  );
};
